import {
  createParser,
  debounce,
  parseAsArrayOf,
  parseAsString,
  parseAsStringLiteral,
  useQueryStates,
} from "nuqs";
import { useMemo } from "react";
import useAuth from "@/core/auth/use-auth";
import { useDebounce } from "@/core/hooks";
import { AP_ACTION_TYPE, type ApActionType, type ApDayRange } from "./types";

export const AP_ASSIGNEE_ME = "me";

const DAY = /^\d{4}-\d{2}-\d{2}$/;

const parseAsDayRange = createParser<ApDayRange>({
  parse: (value) => {
    const [from, to = from] = value.split("~");
    if (!DAY.test(from) || !DAY.test(to) || from > to) return null;
    return { from, to };
  },
  serialize: ({ from, to }) => (from === to ? from : `${from}~${to}`),
  eq: (a, b) => a.from === b.from && a.to === b.to,
});

const ACTION_TYPES = Object.values(AP_ACTION_TYPE);

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// The API refuses a malformed vendor id, which would fail the whole queue;
// parseAsArrayOf drops the items this rejects, so a hand-edited or truncated
// one is simply ignored.
const parseAsVendorId = createParser({
  parse: (value) => (UUID.test(value) ? value : null),
  serialize: (value: string) => value,
});

const ownerAndActionParsers = {
  action: parseAsArrayOf(parseAsStringLiteral(ACTION_TYPES)).withDefault([]),
  assignee: parseAsArrayOf(parseAsString).withDefault([]),
};

const reviewQueueParsers = {
  ...ownerAndActionParsers,
  vendor: parseAsArrayOf(parseAsVendorId).withDefault([]),
  q: parseAsString.withDefault(""),
};

const invoiceListParsers = {
  ...ownerAndActionParsers,
  status: parseAsString.withDefault("all"),
  q: parseAsString.withDefault(""),
  date: parseAsStringLiteral(["added", "reviewed"] as const).withDefault(
    "added",
  ),
  days: parseAsDayRange,
};

const urlOptions = {
  history: "replace",
  scroll: false,
  shallow: false,
} as const;

const useResolvedAssignees = (assignee: string[]) => {
  const { user } = useAuth();
  return useMemo(
    () =>
      assignee
        .map((token) => (token === AP_ASSIGNEE_ME ? user?.id : token))
        .filter((id): id is string => !!id),
    [assignee, user?.id],
  );
};

const searchUrlUpdate = (q: string) => ({
  limitUrlUpdates: q ? debounce(300) : undefined,
});

/** The Review view's filters, shared with the review page's queue carousel. */
export const useReviewQueueFilters = () => {
  const [{ action, assignee, vendor, q }, setParams] = useQueryStates(
    reviewQueueParsers,
    urlOptions,
  );
  const assigneeIds = useResolvedAssignees(assignee);
  const search = useDebounce(q, 300);
  return {
    actionTypes: action as ApActionType[],
    assigneeTokens: assignee,
    vendorIds: vendor,
    search: q,
    /** False while the typed search waits out the debounce to the queue. */
    searchSettled: q === search,
    // By the debounced search, as the queue is: by the typed one, clearing a
    // search that matched nothing would read as an empty unfiltered queue
    // ("All caught up") until the debounce caught up.
    filtered:
      action.length > 0 ||
      assignee.length > 0 ||
      vendor.length > 0 ||
      search.trim() !== "",
    apiFilters: useMemo(
      () => ({ actionTypes: action, assigneeIds, vendorIds: vendor, search }),
      [action, assigneeIds, vendor, search],
    ),
    setActionTypes: (types: ApActionType[]) => setParams({ action: types }),
    setAssigneeTokens: (tokens: string[]) => setParams({ assignee: tokens }),
    setVendorIds: (ids: string[]) => setParams({ vendor: ids }),
    setSearch: (value: string) =>
      setParams({ q: value }, searchUrlUpdate(value)),
    clear: () =>
      setParams({ action: null, assignee: null, vendor: null, q: null }),
  };
};

export const useInvoiceListParams = () => {
  const [params, setParams] = useQueryStates(invoiceListParsers, urlOptions);
  const assigneeIds = useResolvedAssignees(params.assignee);
  return {
    params,
    assigneeIds,
    setParams,
    setSearch: (q: string) => setParams({ q }, searchUrlUpdate(q)),
  };
};

export type InvoiceListParams = ReturnType<
  typeof useInvoiceListParams
>["params"];
